import React from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  WrenchIcon,
  Bars3Icon,
  ScaleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import useStore from '../store/useStore';
import AiAssistant from './AiAssistant';

const navItems = [
  { name: 'Knowledge Base', path: '/knowledge-base', icon: BookOpenIcon },
  { name: 'Workbench', path: '/workbench', icon: WrenchIcon }
];

function Layout() {
  const { user, sidebarOpen, toggleSidebar, aiAssistantOpen, toggleAiAssistant } = useStore();
  const location = useLocation();

  return (
    <div className="flex h-screen bg-galaxy-950">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 240 : 72 }}
        className="bg-galaxy-900/50 border-r border-galaxy-800/50 flex flex-col"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-galaxy-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center">
              <ScaleIcon className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="overflow-hidden"
              >
                <div className="text-sm font-semibold text-white whitespace-nowrap">Legal System</div>
                <div className="text-xs text-galaxy-400 whitespace-nowrap">Document Management</div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path === '/knowledge-base' && location.pathname === '/') ||
              (item.path === '/workbench' && location.pathname.startsWith('/workbench'));

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-galaxy-600 text-white'
                    : 'text-galaxy-300 hover:bg-galaxy-800/50 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="text-sm font-medium whitespace-nowrap">{item.name}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-galaxy-800/50 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="p-2 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded-lg transition-colors"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">
                {navItems.find(item => location.pathname.startsWith(item.path))?.name || 'Legal System'}
              </h1>
              <p className="text-xs text-galaxy-400">Document Management</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* AI Assistant Toggle */}
            <button
              onClick={toggleAiAssistant}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                aiAssistantOpen
                  ? 'bg-galaxy-600 text-white'
                  : 'text-galaxy-400 hover:text-white hover:bg-galaxy-800/50'
              }`}
              title="Toggle AI Assistant"
            >
              <SparklesIcon className="w-5 h-5" />
              <span className="text-sm font-medium">AI Assistant</span>
            </button>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-white">{user?.name || 'Demo User'}</div>
                <div className="text-xs text-galaxy-400">{user?.title || 'Legal Professional'}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.name?.split(' ').map(n => n[0]).join('') || 'DU'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* AI Assistant Sidebar */}
      <AnimatePresence>
        {aiAssistantOpen && <AiAssistant />}
      </AnimatePresence>
    </div>
  );
}

export default Layout;
